import { useRef, useState } from 'react';
import { uploadCbcApplicantTranslation } from '../lib/portal';

export interface CbcTranslationFallbackCardProps {
  requirementId: string;
  detectedLanguage: string;
  onUploaded: () => void;
  onError: (message: string) => void;
}

/**
 * Unsupported-language CBC fallback (Asana 1217709826504784): rendered when
 * the Hub fences a criminal-background record into
 * `awaiting_applicant_translation` — the detected language is outside the
 * AI-translation allowlist, so no translation was attempted. The applicant
 * uploads their own English version here instead of waiting on a
 * translation that was never going to happen.
 */
export function CbcTranslationFallbackCard({
  requirementId,
  detectedLanguage,
  onUploaded,
  onError,
}: CbcTranslationFallbackCardProps) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadCbcApplicantTranslation(requirementId, file);
      onUploaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div
      className="globus-banner"
      role="status"
      data-testid="banner-cbc-translation-fallback"
    >
      <p style={{ margin: 0 }}>
        Translation is not yet available for <strong>{detectedLanguage}</strong>. Please upload
        an English version of this document.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        disabled={uploading}
        data-testid="input-cbc-translation-fallback"
        onChange={(e) => void handleFile(e.target.files?.[0])}
        style={{ marginTop: '0.5rem' }}
      />
      {uploading && <span style={{ marginLeft: '0.5rem' }}>Uploading…</span>}
    </div>
  );
}
